import { RCU_LIMITS } from './limits';

/** How bad a configuration problem is. */
export const SEVERITY = {
  /** The configuration cannot be deployed. */
  ERROR: 'error',
  /** Usable, but something is probably unintended. */
  WARNING: 'warning',
  /** Worth knowing. */
  INFO: 'info',
} as const;

export type Severity = (typeof SEVERITY)[keyof typeof SEVERITY];

/** Which part of the configuration an issue points at, for deep links. */
export const ISSUE_TARGET = {
  RCU: 'rcu',
  RAIL: 'rail',
  LOAD: 'load',
  ROOM: 'room',
  BRIDGE: 'bridge',
} as const;

export type IssueTargetKind = (typeof ISSUE_TARGET)[keyof typeof ISSUE_TARGET];

export interface IssueTarget {
  readonly kind: IssueTargetKind;
  /** Entity id when the issue is about one entity. */
  readonly id?: string;
}

export const ISSUE = {
  // Referential failures — an operation targeted something that is not there.
  RAIL_NOT_FOUND: 'rail_not_found',
  LOAD_NOT_FOUND: 'load_not_found',
  ROOM_NOT_FOUND: 'room_not_found',
  NAME_REQUIRED: 'name_required',

  // Hardware limits.
  OUTPUT_COUNT_OUT_OF_RANGE: 'output_count_out_of_range',
  OUTPUT_COUNT_BLOCKED: 'output_count_blocked',
  RAIL_ADDRESS_OUT_OF_RANGE: 'rail_address_out_of_range',
  RAIL_ADDRESS_CONFLICT: 'rail_address_conflict',
  RAIL_TYPE_LIMIT: 'rail_type_limit',
  MODULE_TOTAL_LIMIT: 'module_total_limit',
  DALI_BRIDGE_NOT_FOUND: 'dali_bridge_not_found',
  DALI_BRIDGE_LIMIT: 'dali_bridge_limit',
  DALI_BRIDGE_ADDRESS_CONFLICT: 'dali_bridge_address_conflict',
  DALI_BRIDGE_MASTER_CONFLICT: 'dali_bridge_master_conflict',

  // Wiring problems.
  CHANNEL_OUT_OF_RANGE: 'channel_out_of_range',
  UNKNOWN_RAIL: 'unknown_rail',
  CHANNEL_OCCUPIED: 'channel_occupied',
  DUPLICATE_CHANNEL: 'duplicate_channel',
  INCOMPATIBLE_LOAD: 'incompatible_load',

  // Configuration hygiene.
  UNWIRED_LOAD: 'unwired_load',
  ORPHAN_ROOM: 'orphan_room',
  NO_ROOMS: 'no_rooms',
  EMPTY_CONFIG: 'empty_config',

  // Command channel.
  NO_RCU_ADDRESS: 'no_rcu_address',
  COMMAND_UNSUPPORTED: 'command_unsupported',
  COMMAND_FIELD_OUT_OF_RANGE: 'command_field_out_of_range',
  COMMAND_TOO_LARGE: 'command_too_large',
  COMMAND_REPLY_UNREADABLE: 'command_reply_unreadable',

  // Console operations.
  IMPORT_UNREADABLE: 'import_unreadable',
  IMPORT_INVALID: 'import_invalid',
} as const;

export type IssueCode = (typeof ISSUE)[keyof typeof ISSUE];

/** One problem found in a configuration. */
export interface ConfigIssue {
  readonly code: IssueCode;
  readonly target: IssueTarget;
  /**
   * Human-readable specifics, e.g. `Phase cut address 3`. Messages embed it,
   * which keeps one template per code instead of one per call site.
   */
  readonly detail?: string;
}

/** Build an issue, e.g. `configIssue(ISSUE.UNWIRED_LOAD, { kind: ISSUE_TARGET.LOAD, id }, name)`. */
export const configIssue = (
  code: IssueCode,
  target: IssueTarget,
  detail?: string,
): ConfigIssue => ({ code, target, detail });

/** Every code's severity, in one place so a rule cannot pick its own. */
export const ISSUE_SEVERITY = {
  [ISSUE.RAIL_NOT_FOUND]: SEVERITY.ERROR,
  [ISSUE.LOAD_NOT_FOUND]: SEVERITY.ERROR,
  [ISSUE.ROOM_NOT_FOUND]: SEVERITY.ERROR,
  [ISSUE.NAME_REQUIRED]: SEVERITY.ERROR,
  [ISSUE.OUTPUT_COUNT_OUT_OF_RANGE]: SEVERITY.ERROR,
  [ISSUE.OUTPUT_COUNT_BLOCKED]: SEVERITY.ERROR,
  [ISSUE.RAIL_ADDRESS_OUT_OF_RANGE]: SEVERITY.ERROR,
  [ISSUE.RAIL_ADDRESS_CONFLICT]: SEVERITY.ERROR,
  [ISSUE.RAIL_TYPE_LIMIT]: SEVERITY.ERROR,
  [ISSUE.MODULE_TOTAL_LIMIT]: SEVERITY.ERROR,
  [ISSUE.DALI_BRIDGE_NOT_FOUND]: SEVERITY.ERROR,
  [ISSUE.DALI_BRIDGE_LIMIT]: SEVERITY.ERROR,
  [ISSUE.DALI_BRIDGE_ADDRESS_CONFLICT]: SEVERITY.ERROR,
  [ISSUE.DALI_BRIDGE_MASTER_CONFLICT]: SEVERITY.ERROR,
  [ISSUE.CHANNEL_OUT_OF_RANGE]: SEVERITY.ERROR,
  [ISSUE.UNKNOWN_RAIL]: SEVERITY.ERROR,
  [ISSUE.CHANNEL_OCCUPIED]: SEVERITY.ERROR,
  [ISSUE.DUPLICATE_CHANNEL]: SEVERITY.ERROR,
  [ISSUE.INCOMPATIBLE_LOAD]: SEVERITY.ERROR,
  [ISSUE.UNWIRED_LOAD]: SEVERITY.WARNING,
  [ISSUE.ORPHAN_ROOM]: SEVERITY.WARNING,
  [ISSUE.NO_ROOMS]: SEVERITY.INFO,
  [ISSUE.EMPTY_CONFIG]: SEVERITY.INFO,
  [ISSUE.NO_RCU_ADDRESS]: SEVERITY.WARNING,
  [ISSUE.COMMAND_UNSUPPORTED]: SEVERITY.ERROR,
  [ISSUE.COMMAND_FIELD_OUT_OF_RANGE]: SEVERITY.ERROR,
  [ISSUE.COMMAND_TOO_LARGE]: SEVERITY.ERROR,
  [ISSUE.COMMAND_REPLY_UNREADABLE]: SEVERITY.WARNING,
  [ISSUE.IMPORT_UNREADABLE]: SEVERITY.ERROR,
  [ISSUE.IMPORT_INVALID]: SEVERITY.ERROR,
} as const satisfies Record<IssueCode, Severity>;

const ISSUE_MESSAGES = {
  [ISSUE.RAIL_NOT_FOUND]: (detail) => `Rail not found: ${detail}.`,
  [ISSUE.LOAD_NOT_FOUND]: (detail) => `Load not found: ${detail}.`,
  [ISSUE.ROOM_NOT_FOUND]: (detail) => `Room not found: ${detail}.`,
  [ISSUE.NAME_REQUIRED]: (detail) => `${detail} name is required.`,
  [ISSUE.OUTPUT_COUNT_OUT_OF_RANGE]: (detail) =>
    `Onboard output count ${detail} is outside ${RCU_LIMITS.MIN_OUTPUTS}–${RCU_LIMITS.MAX_OUTPUTS}.`,
  [ISSUE.OUTPUT_COUNT_BLOCKED]: (detail) => `Cannot reduce onboard outputs: ${detail}.`,
  [ISSUE.RAIL_ADDRESS_OUT_OF_RANGE]: (detail) =>
    `Rail address ${detail} is outside ${RCU_LIMITS.MIN_RAIL_ADDRESS}–${RCU_LIMITS.MAX_RAIL_ADDRESS}.`,
  [ISSUE.RAIL_ADDRESS_CONFLICT]: (detail) =>
    `Two rails share ${detail}. Addresses must be unique per rail type.`,
  [ISSUE.RAIL_TYPE_LIMIT]: (detail) =>
    `${detail} rails exceed the limit of ${RCU_LIMITS.MAX_RAILS_PER_TYPE} per RCU.`,
  [ISSUE.MODULE_TOTAL_LIMIT]: (detail) =>
    `This RCU has ${detail} modules; the maximum is ${RCU_LIMITS.MAX_MODULES_TOTAL}.`,
  [ISSUE.DALI_BRIDGE_NOT_FOUND]: (detail) => `DALI bridge not found: ${detail}.`,
  [ISSUE.DALI_BRIDGE_LIMIT]: (detail) =>
    `This RCU already has ${detail} DALI bridges; the maximum is ${RCU_LIMITS.MAX_DALI_BRIDGES}.`,
  [ISSUE.DALI_BRIDGE_ADDRESS_CONFLICT]: (detail) =>
    `Two DALI bridges share address ${detail}. Addresses must be unique.`,
  [ISSUE.DALI_BRIDGE_MASTER_CONFLICT]: (detail) =>
    `Two DALI bridges share master ${detail}. One bus carries one bridge.`,
  [ISSUE.CHANNEL_OUT_OF_RANGE]: (detail) => `Wiring out of range: ${detail}.`,
  [ISSUE.UNKNOWN_RAIL]: (detail) => `Wiring points at a rail that no longer exists: ${detail}.`,
  [ISSUE.CHANNEL_OCCUPIED]: (detail) => `Cannot wire here: ${detail}.`,
  [ISSUE.DUPLICATE_CHANNEL]: (detail) => `Two loads share ${detail}.`,
  [ISSUE.INCOMPATIBLE_LOAD]: (detail) => `Load type does not match its output: ${detail}.`,
  [ISSUE.UNWIRED_LOAD]: (detail) =>
    `${detail} is not wired to any output, so it cannot be controlled.`,
  [ISSUE.ORPHAN_ROOM]: (detail) => `${detail} is assigned to a room that no longer exists.`,
  [ISSUE.NO_ROOMS]: () => 'No rooms defined yet, so every wired load appears under Unassigned.',
  [ISSUE.EMPTY_CONFIG]: () => 'No hardware defined yet. Add rails and loads to get started.',
  [ISSUE.NO_RCU_ADDRESS]: () =>
    'No IP address or serial number, so commands have nowhere to go. Set one in the RCU details.',
  [ISSUE.COMMAND_UNSUPPORTED]: (detail) => `The RCU has no command for ${detail}.`,
  [ISSUE.COMMAND_FIELD_OUT_OF_RANGE]: (detail) => `${detail} is outside the range the RCU accepts.`,
  [ISSUE.COMMAND_TOO_LARGE]: (detail) =>
    `This command is ${detail}, too large for the RCU's 511-byte command buffer.`,
  [ISSUE.COMMAND_REPLY_UNREADABLE]: (detail) => `Could not read the RCU's reply: ${detail}.`,
  [ISSUE.IMPORT_UNREADABLE]: () =>
    'That data is not a configuration this version of the app understands.',
  [ISSUE.IMPORT_INVALID]: (detail) =>
    `That configuration cannot be applied: ${detail ?? 'it breaks a rule the console enforces'}.`,
} as const satisfies Record<IssueCode, (detail: string | undefined) => string>;

/** The single place issue copy is written. */
export const issueMessage = (issue: ConfigIssue): string =>
  ISSUE_MESSAGES[issue.code](issue.detail);

export const severityOf = (issue: ConfigIssue): Severity => ISSUE_SEVERITY[issue.code];
